import { FetchApi, DiscoveryApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  CertDomain,
  CertDomainCreate,
  CertDomainUpdate,
  CertInventoryAccess,
  CertInventoryApi,
  TlsProbeResult,
} from '@internal/plugin-cert-inventory-common';

export class CertInventoryApiClient implements CertInventoryApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;

  constructor(options: { fetchApi: FetchApi; discoveryApi: DiscoveryApi }) {
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('cert-inventory');
  }

  private async request<T>(
    path: string,
    init?: RequestInit,
  ): Promise<T | undefined> {
    const url = `${await this.baseUrl()}${path}`;
    const response = await this.fetchApi.fetch(url, init);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    if (response.status === 204) {
      return undefined;
    }
    return (await response.json()) as T;
  }

  async listDomains(): Promise<CertDomain[]> {
    return (await this.request<CertDomain[]>('/domains')) ?? [];
  }

  async createDomain(input: CertDomainCreate): Promise<CertDomain> {
    return (await this.request<CertDomain>('/domains', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }))!;
  }

  async updateDomain(
    id: string,
    input: CertDomainUpdate,
  ): Promise<CertDomain> {
    return (await this.request<CertDomain>(`/domains/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }))!;
  }

  async deleteDomain(id: string): Promise<void> {
    await this.request<void>(`/domains/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async refreshDomain(id: string): Promise<CertDomain> {
    return (await this.request<CertDomain>(
      `/domains/${encodeURIComponent(id)}/refresh`,
      { method: 'POST' },
    ))!;
  }

  async checkDomain(input: {
    domain: string;
    port?: number;
  }): Promise<TlsProbeResult> {
    return (await this.request<TlsProbeResult>('/check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }))!;
  }

  async getAccess(): Promise<CertInventoryAccess> {
    return (await this.request<CertInventoryAccess>('/access'))!;
  }
}
